import React, { createContext, useContext, useReducer, useMemo } from 'react';

const AppContext = createContext();

// Action types
const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  SET_USER: 'SET_USER',
  CLEAR_USER: 'CLEAR_USER'
};

// Initial state
const initialState = {
  loading: false,
  error: null,
  notifications: [],
  user: null
};

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case actionTypes.CLEAR_ERROR:
      return { ...state, error: null };
    
    case actionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, {
          id: Date.now(),
          ...action.payload
        }]
      };
    
    case actionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          notification => notification.id !== action.payload
        )
      };
    
    case actionTypes.SET_USER:
      return { ...state, user: action.payload };
    
    case actionTypes.CLEAR_USER:
      return { ...state, user: null };
    
    default:
      return state;
  }
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Action creators
  const actions = useMemo(() => ({
    setLoading: (loading) => dispatch({ type: actionTypes.SET_LOADING, payload: loading }),
    
    setError: (error) => dispatch({ type: actionTypes.SET_ERROR, payload: error }),
    
    clearError: () => dispatch({ type: actionTypes.CLEAR_ERROR }),
    
    addNotification: (notification) => dispatch({ 
      type: actionTypes.ADD_NOTIFICATION, 
      payload: notification 
    }),
    
    removeNotification: (id) => dispatch({ 
      type: actionTypes.REMOVE_NOTIFICATION, 
      payload: id 
    }),
    
    setUser: (user) => dispatch({ type: actionTypes.SET_USER, payload: user }),
    
    clearUser: () => dispatch({ type: actionTypes.CLEAR_USER })
  }), []);

  const contextValue = useMemo(() => ({
    ...state,
    ...actions
  }), [state, actions]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};